import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Paragraph,
  Radio,
  Select,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@navikt/aksel-icons';
import type {
  ActionPlan,
  ActionPlanning,
  Notified,
  PlanArea,
  PlanError,
  PlanFrequency,
} from '../../../api/workEnvironmentTypes';
import {
  useApproveActionPlanMutation,
  useRequestPlanReviewMutation,
  useSaveActionPlanMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { FieldError, validationErrors } from '../assessmentView';
import { FormErrorSummary, TextField } from '../components/FormParts';
import {
  asksForAreas,
  builderStatus,
  coverage,
  dayOf,
  emptyMeasure,
  evaluationBounds,
  approvesAlone,
  hasSafetyRep,
  measureFromProposal,
  PLAN_FIELDS,
  planFieldErrors,
  planForm,
  PlanForm,
  proposalsFor,
  toggleMeasureArea,
  withGoal,
  withoutApprovalTexts,
} from '../planView';
import { areaName, LEVEL_COLORS } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';
import { TakenOverNotice } from '../components/TakenOverNotice';

const FREQUENCIES: PlanFrequency[] = ['WEEKLY', 'BIWEEKLY', 'MONTHLY', 'QUARTERLY'];

type Props = {
  surveyId: number;
  /** Null for a new plan */
  plan: ActionPlan | null;
  /** The areas the plan deals with */
  areas: PlanArea[];
  planning: ActionPlanning;
  onBack: () => void;
  /** The plan went to the safety representative, or was approved, with when the others hear about it */
  onDone: (planId: number, notified: Notified) => void;
};

/**
 * Measures with owner and deadline, a goal, follow-up and evaluation. The team leader sends the
 * plan to the safety representative for approval, or approves it without them with a reason.
 * Saving an approved plan reopens it.
 */
export const PlanBuilder = ({ surveyId, plan, areas, planning, onBack, onDone }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const withSafetyRep = hasSafetyRep(planning);
  const alone = approvesAlone(planning);
  const without = withoutApprovalTexts(planning.reviewMissing, t);
  const [form, setForm] = useState<PlanForm>(() =>
    planForm(
      plan,
      areas.map((area) => area.key),
    ),
  );
  const [planId, setPlanId] = useState<number | null>(plan?.id ?? null);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [asking, setAsking] = useState(false);
  const [deviation, setDeviation] = useState('');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [action, setAction] = useState<'draft' | 'send' | 'approve' | null>(null);
  const save = useSaveActionPlanMutation(surveyId);
  const approve = useApproveActionPlanMutation(surveyId);
  const request = useRequestPlanReviewMutation(surveyId);
  const proposals = proposalsFor(planning.proposals, form);
  const many = asksForAreas(form);
  const areaOf = (factorKey: string) => areas.find((area) => area.key === factorKey);
  const nameOf = (factorKey: string) => {
    const area = areaOf(factorKey);
    return area ? areaName(area, t) : factorKey;
  };
  const today = dayOf();
  const bounds = evaluationBounds();
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;

  const showErrors = (error: unknown) => {
    const invalid = validationErrors<PlanError>(error);
    if (invalid) setErrors(planFieldErrors(invalid, t, nameOf));
  };

  const saveDraft = (then?: (id: number) => void) =>
    save.mutate(
      { planId, plan: form },
      {
        onSuccess: ({ id }) => {
          setPlanId(id);
          setErrors([]);
          setSavedAt(new Date());
          then?.(id);
        },
        onError: showErrors,
      },
    );

  const approveWithout = () => {
    setAction('approve');
    saveDraft((id) =>
      approve.mutate({ planId: id, deviation }, { onSuccess: (notified) => onDone(id, notified), onError: showErrors }),
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!withSafetyRep) return setAsking(true);
    setAction('send');
    saveDraft((id) => request.mutate(id, { onSuccess: (notified) => onDone(id, notified), onError: showErrors }));
  };

  const busy = save.isPending || approve.isPending || request.isPending;
  const loading = (which: typeof action) => busy && action === which;

  const failed = [save, approve, request].find((mutation) => mutation.isError && !validationErrors(mutation.error));

  const setMeasure = (index: number, change: Partial<PlanForm['measures'][number]>) =>
    setForm({
      ...form,
      measures: form.measures.map((measure, i) => (i === index ? { ...measure, ...change } : measure)),
    });

  const usedProposals = form.measures.flatMap((measure) => measure.proposalIds);

  return (
    <form onSubmit={submit} noValidate className={classes.mainColumn}>
      <div>
        <Button type="button" variant="tertiary" data-size="sm" onClick={onBack}>
          <ArrowLeftIcon aria-hidden />
          {t('workEnvironmentPlan.builder.back')}
        </Button>
      </div>
      <FormErrorSummary errors={errors} title={t('workEnvironmentPlan.builder.errorsTitle')} />
      <section aria-label={t('workEnvironmentPlan.builder.areasLabel')} className={process.card}>
        <div className={process.cardBody}>
          <ul className={classes.planAreaList}>
            {areas.map((area) => (
              <li key={area.key}>
                <span className="ds-paragraph" data-size="sm">
                  {areaName(area, t)}
                </span>
                <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
                  {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                </Tag>
              </li>
            ))}
          </ul>
        </div>
      </section>
      {plan?.approved && (
        <Alert data-color="warning" data-size="sm">
          <Paragraph data-size="sm">{t('workEnvironmentPlan.builder.reopenWarning')}</Paragraph>
        </Alert>
      )}

      <section aria-labelledby="plan-measures-heading" className={process.card}>
        <div className={process.cardBody}>
          <div className={process.sideHeading}>
            <Heading level={2} data-size="sm" id="plan-measures-heading">
              {t('workEnvironmentPlan.builder.measuresTitle')}
            </Heading>
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentPlan.builder.measuresIntro')}
            </Paragraph>
          </div>
          {proposals.length > 0 && (
            <div className={classes.areaBody}>
              <Heading level={3} data-size="2xs">
                {t('workEnvironmentPlan.builder.fromTeam')}
              </Heading>
              <ul className={classes.sources}>
                {proposals.map((proposal) => (
                  <li key={proposal.id} className={classes.source}>
                    <span className={classes.areaTitle}>
                      <span className="ds-label" data-size="sm">
                        {proposal.title}
                      </span>
                      <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                        {many
                          ? t('workEnvironmentPlan.builder.proposalMetaArea', {
                              area: nameOf(proposal.factorKey),
                              count: proposal.highlights,
                            })
                          : t('workEnvironmentPlan.builder.proposalMeta', { count: proposal.highlights })}
                      </span>
                    </span>
                    {usedProposals.includes(proposal.id) ? (
                      <span className="ds-paragraph" data-size="xs">
                        {t('workEnvironmentPlan.builder.added')}
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        data-size="sm"
                        onClick={() =>
                          setForm({
                            ...form,
                            measures: [...form.measures, measureFromProposal(proposal, today, form)],
                          })
                        }
                      >
                        {t('workEnvironmentPlan.builder.addProposal')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {form.measures.length === 0 && (
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentPlan.builder.noMeasures')}
            </Paragraph>
          )}
          <ol className={classes.dataAreas}>
            {form.measures.map((measure, index) => (
              <li key={index} className={classes.dataArea}>
                <div className={classes.areaSummary}>
                  <Heading level={3} data-size="2xs">
                    {t('workEnvironmentPlan.builder.measure', { number: index + 1 })}
                  </Heading>
                  <Button
                    type="button"
                    variant="tertiary"
                    data-size="sm"
                    icon
                    aria-label={t('workEnvironmentPlan.builder.remove', { number: index + 1 })}
                    onClick={() => setForm({ ...form, measures: form.measures.filter((_, i) => i !== index) })}
                  >
                    <TrashIcon aria-hidden />
                  </Button>
                </div>
                <Field data-size="sm">
                  <Label htmlFor={PLAN_FIELDS.measureTitle(index)}>{t('workEnvironmentPlan.builder.title')}</Label>
                  <Input
                    id={PLAN_FIELDS.measureTitle(index)}
                    value={measure.title}
                    aria-invalid={Boolean(errorFor(PLAN_FIELDS.measureTitle(index))) || undefined}
                    onChange={(event) => setMeasure(index, { title: event.target.value })}
                  />
                  {errorFor(PLAN_FIELDS.measureTitle(index)) && (
                    <ValidationMessage>{errorFor(PLAN_FIELDS.measureTitle(index))}</ValidationMessage>
                  )}
                </Field>
                <TextField
                  id={`plan-measure-${index}-description`}
                  label={t('workEnvironmentPlan.builder.description')}
                  value={measure.description}
                  onChange={(description) => setMeasure(index, { description })}
                />
                <div className={classes.deadlineRow}>
                  <Field data-size="sm">
                    <Label htmlFor={PLAN_FIELDS.measureOwner(index)}>{t('workEnvironmentPlan.builder.owner')}</Label>
                    <Select
                      id={PLAN_FIELDS.measureOwner(index)}
                      value={measure.ownerId ?? ''}
                      aria-invalid={Boolean(errorFor(PLAN_FIELDS.measureOwner(index))) || undefined}
                      onChange={(event) =>
                        setMeasure(index, { ownerId: event.target.value ? Number(event.target.value) : null })
                      }
                    >
                      <Select.Option value="" disabled>
                        {t('workEnvironmentPlan.builder.chooseOwner')}
                      </Select.Option>
                      {planning.owners.map((owner) => (
                        <Select.Option key={owner.id} value={owner.id}>
                          {t(`workEnvironmentPlan.builder.ownerRole.${owner.role}`, { email: owner.email })}
                        </Select.Option>
                      ))}
                    </Select>
                    {errorFor(PLAN_FIELDS.measureOwner(index)) && (
                      <ValidationMessage>{errorFor(PLAN_FIELDS.measureOwner(index))}</ValidationMessage>
                    )}
                  </Field>
                  <Field data-size="sm">
                    <Label htmlFor={PLAN_FIELDS.measureDue(index)}>{t('workEnvironmentPlan.builder.due')}</Label>
                    <Input
                      id={PLAN_FIELDS.measureDue(index)}
                      type="date"
                      min={today}
                      value={measure.dueDate}
                      aria-invalid={Boolean(errorFor(PLAN_FIELDS.measureDue(index))) || undefined}
                      onChange={(event) => setMeasure(index, { dueDate: event.target.value })}
                    />
                    {errorFor(PLAN_FIELDS.measureDue(index)) && (
                      <ValidationMessage>{errorFor(PLAN_FIELDS.measureDue(index))}</ValidationMessage>
                    )}
                  </Field>
                </div>
                {many && (
                  <Fieldset data-size="sm">
                    <Fieldset.Legend>{t('workEnvironmentPlan.builder.helpsWith')}</Fieldset.Legend>
                    {form.areas.map((area, areaIndex) => (
                      <Checkbox
                        key={area.factorKey}
                        id={`plan-measure-${index}-area-${areaIndex}`}
                        label={nameOf(area.factorKey)}
                        value={area.factorKey}
                        checked={measure.factorKeys.includes(area.factorKey)}
                        aria-invalid={Boolean(errorFor(PLAN_FIELDS.measureAreas(index))) || undefined}
                        onChange={() =>
                          setForm({
                            ...form,
                            measures: form.measures.map((candidate, i) =>
                              i === index ? toggleMeasureArea(candidate, area.factorKey) : candidate,
                            ),
                          })
                        }
                      />
                    ))}
                    {errorFor(PLAN_FIELDS.measureAreas(index)) && (
                      <ValidationMessage>{errorFor(PLAN_FIELDS.measureAreas(index))}</ValidationMessage>
                    )}
                  </Fieldset>
                )}
                {proposals.length > 0 && (
                  <Fieldset data-size="sm">
                    <Fieldset.Legend>{t('workEnvironmentPlan.builder.basedOn')}</Fieldset.Legend>
                    {proposals.map((proposal) => (
                      <Checkbox
                        key={proposal.id}
                        label={proposal.title}
                        value={String(proposal.id)}
                        checked={measure.proposalIds.includes(proposal.id)}
                        onChange={() =>
                          setMeasure(index, {
                            proposalIds: measure.proposalIds.includes(proposal.id)
                              ? measure.proposalIds.filter((id) => id !== proposal.id)
                              : [...measure.proposalIds, proposal.id],
                          })
                        }
                      />
                    ))}
                  </Fieldset>
                )}
              </li>
            ))}
          </ol>
          {many && (
            <div id={PLAN_FIELDS.areaMeasures()} role="status" className={classes.coverage} tabIndex={-1}>
              <span className="ds-label" data-size="sm">
                {t('workEnvironmentPlan.builder.coverageTitle')}
              </span>
              <ul className={classes.planAreaList}>
                {coverage(form).map(({ factorKey, count }) => (
                  <li key={factorKey}>
                    <span className="ds-paragraph" data-size="sm">
                      {nameOf(factorKey)}
                    </span>
                    <Tag data-size="sm" data-color={count > 0 ? 'success' : 'neutral'}>
                      {count > 0
                        ? t('workEnvironmentPlan.builder.coverageCount', { count })
                        : t('workEnvironmentPlan.builder.coverageNone')}
                    </Tag>
                  </li>
                ))}
              </ul>
              <Paragraph data-size="xs" className={process.subtle}>
                {t('workEnvironmentPlan.builder.coverageHelp')}
              </Paragraph>
            </div>
          )}
          <div>
            <Button
              id={PLAN_FIELDS.measures}
              type="button"
              variant="secondary"
              data-size="sm"
              onClick={() => setForm({ ...form, measures: [...form.measures, emptyMeasure(today, form)] })}
            >
              <PlusIcon aria-hidden />
              {t('workEnvironmentPlan.builder.addOwn')}
            </Button>
          </div>
        </div>
      </section>

      <section aria-labelledby="plan-goal-heading" className={process.card}>
        <div className={process.cardBody}>
          <Heading level={2} data-size="sm" id="plan-goal-heading">
            {t('workEnvironmentPlan.builder.goalTitle')}
          </Heading>
          {form.areas.map((area) => (
            <TextField
              key={area.factorKey}
              id={PLAN_FIELDS.goal(area.factorKey)}
              label={
                many
                  ? t('workEnvironmentPlan.builder.goalFor', { name: nameOf(area.factorKey).toLowerCase() })
                  : t('workEnvironmentPlan.builder.goal')
              }
              help={t('workEnvironmentPlan.builder.goalHelp')}
              value={area.goal}
              error={errorFor(PLAN_FIELDS.goal(area.factorKey))}
              onChange={(goal) => setForm(withGoal(form, area.factorKey, goal))}
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="plan-follow-up-heading" className={process.card}>
        <div className={process.cardBody}>
          <div className={process.sideHeading}>
            <Heading level={2} data-size="sm" id="plan-follow-up-heading">
              {t('workEnvironmentPlan.builder.followUpTitle')}
            </Heading>
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentPlan.builder.followUpIntro')}
            </Paragraph>
          </div>
          <Fieldset data-size="sm">
            <Fieldset.Legend>{t('workEnvironmentPlan.builder.frequency')}</Fieldset.Legend>
            {FREQUENCIES.map((frequency) => (
              <Radio
                key={frequency}
                id={`plan-frequency-${frequency}`}
                name="plan-frequency"
                value={frequency}
                label={t(`workEnvironmentPlan.builder.frequencies.${frequency}`)}
                checked={form.frequency === frequency}
                onChange={() => setForm({ ...form, frequency })}
              />
            ))}
            {errorFor(PLAN_FIELDS.frequency) && (
              <ValidationMessage>{errorFor(PLAN_FIELDS.frequency)}</ValidationMessage>
            )}
          </Fieldset>
          <Fieldset data-size="sm">
            <Fieldset.Legend>{t('workEnvironmentPlan.builder.measuring')}</Fieldset.Legend>
            <Checkbox
              label={t('workEnvironmentPlan.builder.safety')}
              description={t('workEnvironmentPlan.builder.safetyHelp')}
              checked={form.measureSafety}
              onChange={() => setForm({ ...form, measureSafety: !form.measureSafety })}
            />
            <Checkbox
              label={t('workEnvironmentPlan.builder.pulse')}
              description={t('workEnvironmentPlan.builder.pulseHelp')}
              checked={form.measurePulse}
              onChange={() => setForm({ ...form, measurePulse: !form.measurePulse })}
            />
          </Fieldset>
        </div>
      </section>

      <section aria-labelledby="plan-evaluation-heading" className={process.card}>
        <div className={process.cardBody}>
          <div className={process.sideHeading}>
            <Heading level={2} data-size="sm" id="plan-evaluation-heading">
              {t('workEnvironmentPlan.builder.evaluationTitle')}
            </Heading>
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentPlan.builder.evaluationIntro')}
            </Paragraph>
          </div>
          <Field data-size="sm">
            <Label htmlFor={PLAN_FIELDS.evaluationDate}>{t('workEnvironmentPlan.builder.evaluationDate')}</Label>
            <Field.Description id={`${PLAN_FIELDS.evaluationDate}-help`}>
              {t('workEnvironmentPlan.builder.evaluationHelp')}
            </Field.Description>
            <Input
              id={PLAN_FIELDS.evaluationDate}
              type="date"
              min={bounds.min}
              max={bounds.max}
              value={form.evaluationDate ?? ''}
              aria-describedby={`${PLAN_FIELDS.evaluationDate}-help`}
              aria-invalid={Boolean(errorFor(PLAN_FIELDS.evaluationDate)) || undefined}
              onChange={(event) => setForm({ ...form, evaluationDate: event.target.value || null })}
            />
            {errorFor(PLAN_FIELDS.evaluationDate) && (
              <ValidationMessage>{errorFor(PLAN_FIELDS.evaluationDate)}</ValidationMessage>
            )}
          </Field>
        </div>
      </section>

      <div className={process.card}>
        <div className={process.cardBody}>
          {failed && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentPlan.builder.failed')}</Paragraph>
            </Alert>
          )}
          {asking && (
            <div className={classes.areaBody}>
              <Heading level={2} data-size="2xs">
                {t('workEnvironmentPlan.builder.without.title')}
              </Heading>
              <Paragraph data-size="sm">{without.text}</Paragraph>
              <TextField
                id={PLAN_FIELDS.deviation}
                label={t('workEnvironmentPlan.builder.without.reason')}
                help={without.reasonHelp}
                value={deviation}
                error={errorFor(PLAN_FIELDS.deviation)}
                onChange={setDeviation}
              />
              <div className={process.linkRow}>
                <Button
                  type="button"
                  variant="primary"
                  data-size="sm"
                  loading={loading('approve')}
                  onClick={approveWithout}
                >
                  {t('workEnvironmentPlan.builder.approveWithout')}
                </Button>
                <Button type="button" variant="tertiary" data-size="sm" onClick={() => setAsking(false)}>
                  {t('workEnvironmentPlan.builder.without.cancel')}
                </Button>
              </div>
            </div>
          )}
          <Paragraph data-size="sm" role="status">
            {builderStatus(form, savedAt, t, i18n.language)}
          </Paragraph>
          {!asking && withSafetyRep && <TakenOverNotice emails={planning.takenOverBy} />}
          {!asking && planning.reviewMissing && !alone && (
            <Paragraph data-size="sm" className={process.subtle}>
              {t(`workEnvironmentPlan.builder.reviewMissing.${planning.reviewMissing}`)}
            </Paragraph>
          )}
          {!asking && (
            <div className={classes.planActions}>
              {withSafetyRep && (
                <Button type="submit" variant="primary" data-size="sm" loading={loading('send')}>
                  {t('workEnvironmentPlan.builder.send')}
                </Button>
              )}
              {alone ? (
                <Button
                  type="button"
                  variant="primary"
                  data-size="sm"
                  loading={loading('approve')}
                  onClick={approveWithout}
                >
                  {t('workEnvironmentPlan.builder.approve')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={withSafetyRep ? 'secondary' : 'primary'}
                  data-size="sm"
                  onClick={() => setAsking(true)}
                >
                  {t('workEnvironmentPlan.builder.approveWithout')}
                </Button>
              )}
              <Button
                type="button"
                variant="tertiary"
                data-size="sm"
                loading={loading('draft')}
                onClick={() => {
                  setAction('draft');
                  saveDraft();
                }}
              >
                {t('workEnvironmentPlan.builder.saveDraft')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};
